import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import ConfirmationModal from '@/components/ConfirmationModal';
import {
  RefreshCw, Plus, Trash2, Play, Shield, AlertTriangle, Bug,
  ArrowLeft, Eye, X, ChevronDown, Search, Activity, Server,
  AlertOctagon, Info,
} from 'lucide-react';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-800',
  high: 'bg-orange-100 text-orange-800',
  medium: 'bg-yellow-100 text-yellow-800',
  low: 'bg-blue-100 text-blue-800',
  info: 'bg-gray-100 text-gray-600',
};

const SEVERITY_DOT_COLORS: Record<string, string> = {
  critical: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-yellow-500',
  low: 'bg-blue-500',
  info: 'bg-gray-400',
};

const STATUS_COLORS: Record<string, string> = {
  idle: 'bg-gray-100 text-gray-600',
  running: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
};

const SCAN_TYPES = ['vulnerability', 'compliance', 'configuration', 'malware', 'network', 'web_application', 'database', 'api'];
const TARGET_TYPES = ['ip', 'domain', 'url', 'network_range', 'database', 'api_endpoint', 'file_system'];
const SCHEDULES = ['once', 'daily', 'weekly', 'monthly'];

interface Scan {
  id: number;
  name: string;
  scan_type: string;
  target_type: string;
  target_value: string;
  schedule: string;
  status: string;
  description?: string;
  last_run?: string;
  next_run?: string;
  created_at: string;
}

interface Vulnerability {
  id: number;
  scan_id: number;
  title: string;
  description: string;
  severity: string;
  cve_id?: string;
  cvss_score?: number;
  location?: string;
  remediation?: string;
  impact_confidentiality?: string;
  impact_integrity?: string;
  impact_availability?: string;
  references?: string[];
  status?: string;
}

interface Risk {
  id: number;
  title: string;
  description: string;
  severity: string;
  likelihood: string;
  impact: string;
  status: string;
  mitigation?: string;
  mitigated_at?: string;
}

type View = 'list' | 'results' | 'risks';

const SecurityScanningDashboard: React.FC = () => {
  const { user } = useAuth();
  const [view, setView] = useState<View>('list');
  const [scans, setScans] = useState<Scan[]>([]);
  const [vulnerabilities, setVulnerabilities] = useState<Vulnerability[]>([]);
  const [risks, setRisks] = useState<Risk[]>([]);
  const [selectedScan, setSelectedScan] = useState<Scan | null>(null);
  const [scanResults, setScanResults] = useState<Vulnerability[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({ total: 0, running: 0, vulnCount: 0, criticalCount: 0, highCount: 0 });

  // Create modal
  const [createModal, setCreateModal] = useState(false);
  const [form, setForm] = useState({ name: '', scan_type: 'vulnerability', target_type: 'ip', target_value: '', schedule: 'once', description: '' });
  const [submitting, setSubmitting] = useState(false);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState<Scan | null>(null);

  // Vuln detail modal
  const [selectedVuln, setSelectedVuln] = useState<Vulnerability | null>(null);

  // Mitigation modal
  const [mitigateRisk, setMitigateRisk] = useState<Risk | null>(null);
  const [mitigateNotes, setMitigateNotes] = useState('');

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [scanRes, vulnRes, riskRes] = await Promise.all([
        fetch('/api/security-scanning/scans', { headers: authHeaders() }),
        fetch('/api/security-scanning/vulnerabilities', { headers: authHeaders() }),
        fetch('/api/security-scanning/risks', { headers: authHeaders() }),
      ]);

      let scanList: Scan[] = [];
      let vulnList: Vulnerability[] = [];
      let riskList: Risk[] = [];

      if (scanRes.ok) {
        try {
          const scanData = await scanRes.json();
          const raw = scanData?.scans ?? scanData;
          scanList = Array.isArray(raw) ? raw : [];
        } catch { scanList = []; }
      }

      if (vulnRes.ok) {
        try {
          const vulnData = await vulnRes.json();
          const raw = vulnData?.vulnerabilities ?? vulnData;
          vulnList = Array.isArray(raw) ? raw : [];
        } catch { vulnList = []; }
      }

      if (riskRes.ok) {
        try {
          const riskData = await riskRes.json();
          const raw = riskData?.risks ?? riskData;
          riskList = Array.isArray(raw) ? raw : [];
        } catch { riskList = []; }
      }

      setScans(scanList);
      setVulnerabilities(vulnList);
      setRisks(riskList);

      setStats({
        total: scanList.length,
        running: scanList.filter((s: Scan) => s.status === 'running').length,
        vulnCount: vulnList.length,
        criticalCount: vulnList.filter((v: Vulnerability) => v.severity === 'critical').length,
        highCount: vulnList.filter((v: Vulnerability) => v.severity === 'high').length,
      });
    } catch (e: any) {
      setScans([]);
      setVulnerabilities([]);
      setRisks([]);
      setStats({ total: 0, running: 0, vulnCount: 0, criticalCount: 0, highCount: 0 });
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openResults = async (scan: Scan) => {
    setSelectedScan(scan);
    setView('results');
    try {
      const res = await fetch(`/api/security-scanning/scans/${scan.id}/results`, { headers: authHeaders() });
      if (!res.ok) { setScanResults([]); return; }
      const data = await res.json();
      const raw = data?.results ?? data?.vulnerabilities ?? data;
      setScanResults(Array.isArray(raw) ? raw : []);
    } catch (e: any) {
      setScanResults([]);
      setError(e.message);
    }
  };

  const createScan = async () => {
    if (!form.name.trim() || !form.target_value.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch('/api/security-scanning/scans', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Failed to create scan');
      setCreateModal(false);
      setForm({ name: '', scan_type: 'vulnerability', target_type: 'ip', target_value: '', schedule: 'once', description: '' });
      loadAll();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const runScan = async (scanId: number) => {
    try {
      await fetch(`/api/security-scanning/scans/${scanId}/execute`, { method: 'POST', headers: authHeaders() });
      setScans(prev => prev.map(s => s.id === scanId ? { ...s, status: 'running' } : s));
    } catch (e: any) {
      setError(e.message);
    }
  };

  const deleteScan = async () => {
    if (!deleteTarget) return;
    try {
      await fetch(`/api/security-scanning/scans/${deleteTarget.id}`, { method: 'DELETE', headers: authHeaders() });
      setDeleteTarget(null);
      if (selectedScan?.id === deleteTarget.id) { setView('list'); setSelectedScan(null); }
      loadAll();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const recordMitigation = async () => {
    if (!mitigateRisk) return;
    setSubmitting(true);
    try {
      await fetch(`/api/security-scanning/risks/${mitigateRisk.id}/mitigate`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ mitigation: mitigateNotes }),
      });
      setMitigateRisk(null);
      setMitigateNotes('');
      loadAll();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const STAT_CARDS = [
    { label: 'Total Scans', value: stats.total, icon: Shield, color: 'text-blue-600' },
    { label: 'Running', value: stats.running, icon: Activity, color: 'text-blue-600' },
    { label: 'Vulnerabilities', value: stats.vulnCount, icon: Bug, color: 'text-purple-600' },
    { label: 'Critical', value: stats.criticalCount, icon: AlertOctagon, color: 'text-red-600' },
    { label: 'High', value: stats.highCount, icon: AlertTriangle, color: 'text-orange-600' },
  ];

  if (loading && scans.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {view !== 'list' && (
            <button onClick={() => { setView('list'); setSelectedScan(null); }} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={20} />
            </button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {view === 'results' ? `Results: ${selectedScan?.name}` : view === 'risks' ? 'Security Risks' : 'Security Scanning'}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {view === 'list' ? 'Manage vulnerability scans and security assessments' : view === 'results' ? 'Vulnerabilities found in this scan' : 'Risk register and mitigation tracking'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={loadAll} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">
            <RefreshCw size={16} />
          </button>
          {view === 'list' && (
            <>
              <button onClick={() => setView('risks')} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-700 border rounded-lg hover:bg-gray-50">
                <Shield size={16} /> Risk Register
              </button>
              <button onClick={() => setCreateModal(true)} className="flex items-center gap-1 px-3 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                <Plus size={16} /> New Scan
              </button>
            </>
          )}
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}

      {/* Stats */}
      {view === 'list' && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {STAT_CARDS.map(s => (
            <div key={s.label} className="bg-white border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <s.icon size={16} className={s.color} />
                <p className="text-xs text-gray-500">{s.label}</p>
              </div>
              <p className="text-2xl font-bold text-gray-900">{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* SCAN LIST */}
      {view === 'list' && (
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Schedule</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Run</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Next Run</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {scans.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">No scans configured</td></tr>
                ) : scans.map(scan => (
                  <tr key={scan.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <button onClick={() => openResults(scan)} className="text-blue-600 hover:underline font-medium">
                        {scan.name}
                      </button>
                    </td>
                    <td className="px-4 py-3 capitalize text-gray-600">{scan.scan_type.replace('_', ' ')}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-500">{scan.target_type}:</span>{' '}
                      <span className="font-mono text-xs">{scan.target_value}</span>
                    </td>
                    <td className="px-4 py-3 capitalize text-gray-600">{scan.schedule}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[scan.status] || 'bg-gray-100 text-gray-600'}`}>
                        {scan.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {scan.last_run ? new Date(scan.last_run).toLocaleString() : '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {scan.next_run ? new Date(scan.next_run).toLocaleString() : '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => runScan(scan.id)} disabled={scan.status === 'running'} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded disabled:opacity-40" title="Run Scan">
                          <Play size={14} />
                        </button>
                        <button onClick={() => openResults(scan)} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded" title="View Results">
                          <Eye size={14} />
                        </button>
                        <button onClick={() => setDeleteTarget(scan)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Delete">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* SCAN RESULTS */}
      {view === 'results' && (
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">CVE</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">CVSS</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Remediation</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Detail</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {scanResults.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No vulnerabilities found</td></tr>
                ) : scanResults.map(v => (
                  <tr key={v.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${SEVERITY_COLORS[v.severity] || SEVERITY_COLORS.info}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${SEVERITY_DOT_COLORS[v.severity] || SEVERITY_DOT_COLORS.info}`} />
                        {v.severity}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900 max-w-[200px] truncate">{v.title}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-600">{v.cve_id || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">{v.cvss_score != null ? v.cvss_score.toFixed(1) : '-'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs truncate max-w-[150px]">{v.location || '-'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs truncate max-w-[200px]">{v.remediation || '-'}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setSelectedVuln(v)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded">
                        <Eye size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* RISK REGISTER */}
      {view === 'risks' && (
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risk</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Likelihood</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Impact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mitigation</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {risks.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No security risks recorded</td></tr>
                ) : risks.map(risk => (
                  <tr key={risk.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{risk.title}</p>
                      <p className="text-xs text-gray-500 truncate max-w-[250px]">{risk.description}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${SEVERITY_COLORS[risk.severity] || SEVERITY_COLORS.info}`}>
                        {risk.severity}
                      </span>
                    </td>
                    <td className="px-4 py-3 capitalize text-gray-600">{risk.likelihood || '-'}</td>
                    <td className="px-4 py-3 capitalize text-gray-600">{risk.impact || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${risk.status === 'mitigated' ? 'bg-green-100 text-green-800' : risk.status === 'accepted' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'}`}>
                        {risk.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs truncate max-w-[200px]">{risk.mitigation || '-'}</td>
                    <td className="px-4 py-3 text-right">
                      {risk.status !== 'mitigated' && (
                        <button onClick={() => { setMitigateRisk(risk); setMitigateNotes(risk.mitigation || ''); }} className="text-xs text-blue-600 hover:text-blue-800">
                          Record Mitigation
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Scan Modal */}
      {createModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">New Scan</h2>
              <button onClick={() => setCreateModal(false)} className="p-1 hover:bg-gray-100 rounded"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Scan Name</label>
                <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Production Server Scan" className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Scan Type</label>
                  <select value={form.scan_type} onChange={e => setForm({ ...form, scan_type: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm">
                    {SCAN_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Target Type</label>
                  <select value={form.target_type} onChange={e => setForm({ ...form, target_type: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm">
                    {TARGET_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Value</label>
                <input type="text" value={form.target_value} onChange={e => setForm({ ...form, target_value: e.target.value })} placeholder="e.g. 192.168.1.0/24 or example.com" className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Schedule</label>
                <select value={form.schedule} onChange={e => setForm({ ...form, schedule: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm">
                  {SCHEDULES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setCreateModal(false)} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={createScan} disabled={submitting || !form.name.trim() || !form.target_value.trim()} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {submitting ? 'Creating...' : 'Create Scan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Vulnerability Detail Modal */}
      {selectedVuln && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-xl p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${SEVERITY_COLORS[selectedVuln.severity]}`}>
                  {selectedVuln.severity}
                </span>
                <h2 className="text-lg font-semibold text-gray-900">{selectedVuln.title}</h2>
              </div>
              <button onClick={() => setSelectedVuln(null)} className="p-1 hover:bg-gray-100 rounded"><X size={18} /></button>
            </div>

            <div className="space-y-4 text-sm">
              <div>
                <h4 className="font-medium text-gray-700 mb-1">Description</h4>
                <p className="text-gray-600">{selectedVuln.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {selectedVuln.cve_id && (
                  <div><span className="text-gray-500">CVE ID:</span> <span className="font-mono">{selectedVuln.cve_id}</span></div>
                )}
                {selectedVuln.cvss_score != null && (
                  <div><span className="text-gray-500">CVSS Score:</span> <span className="font-bold">{selectedVuln.cvss_score.toFixed(1)}</span></div>
                )}
                {selectedVuln.location && (
                  <div className="col-span-2"><span className="text-gray-500">Location:</span> <span className="font-mono text-xs">{selectedVuln.location}</span></div>
                )}
              </div>

              {(selectedVuln.impact_confidentiality || selectedVuln.impact_integrity || selectedVuln.impact_availability) && (
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Impact Assessment</h4>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { label: 'Confidentiality', value: selectedVuln.impact_confidentiality },
                      { label: 'Integrity', value: selectedVuln.impact_integrity },
                      { label: 'Availability', value: selectedVuln.impact_availability },
                    ].map(i => i.value && (
                      <div key={i.label} className="bg-gray-50 rounded-lg p-2 text-center">
                        <p className="text-xs text-gray-500">{i.label}</p>
                        <p className="font-medium capitalize">{i.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedVuln.remediation && (
                <div>
                  <h4 className="font-medium text-gray-700 mb-1">Remediation Steps</h4>
                  <p className="text-gray-600 bg-green-50 border border-green-200 rounded-lg p-3">{selectedVuln.remediation}</p>
                </div>
              )}

              {selectedVuln.references && selectedVuln.references.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-700 mb-1">References</h4>
                  <ul className="space-y-1">
                    {selectedVuln.references.map((ref, i) => (
                      <li key={i}>
                        <a href={ref} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs break-all">{ref}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-5">
              <button onClick={() => setSelectedVuln(null)} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Mitigation Modal */}
      {mitigateRisk && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Record Mitigation</h2>
            <p className="text-sm text-gray-500 mb-4">{mitigateRisk.title}</p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mitigation Notes</label>
            <textarea value={mitigateNotes} onChange={e => setMitigateNotes(e.target.value)} rows={4} className="w-full border rounded-lg px-3 py-2 text-sm mb-4" placeholder="Describe the mitigation actions taken..." />
            <div className="flex justify-end gap-2">
              <button onClick={() => setMitigateRisk(null)} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={recordMitigation} disabled={submitting || !mitigateNotes.trim()} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {submitting ? 'Saving...' : 'Save Mitigation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={deleteScan}
        title="Delete Scan"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? All results and history will be permanently removed.`}
        type="danger"
        confirmText="Delete"
      />
    </div>
  );
};

export default SecurityScanningDashboard;
